import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    BookOpen, Clock, Users, Tag, FileText, Download,
    CheckCircle, Video, ChevronDown, ChevronUp,
    AlertCircle, Target, Play,
    Layers, BarChart2, Info, X, ArrowLeft,
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';

// ─── Brand colour tokens (matches tailwind.config.js) ───────────────────────
const C = {
    // Primary – green
    green: '#22c55e',
    greenDark: '#15803d',
    greenMid: '#16a34a',
    green50: '#f0fdf4',
    green100: '#dcfce7',
    green700: '#15803d',

    // Secondary – blue
    blue: '#3b82f6',
    blueDark: '#0B2033',
    blueDarker: '#1e40af',
    blue50: '#eff6ff',
    blue100: '#dbeafe',
    blue700: '#0B2033',

    // Neutrals
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827',

    // Semantics
    yellow: '#f59e0b',
    yellowLight: '#fef9c3',
    yellowDark: '#854d0e',
    red: '#ef4444',
    redLight: '#fee2e2',
    redDark: '#b91c1c',
    purple: '#7c3aed',
    purpleLight: '#f3e8ff',
    orange: '#c2410c',
    orangeLight: '#ffedd5',

    // Hero header bg
    heroBg: 'linear-gradient(135deg, #064e3b 0%, #065f46 40%, #15803d 100%)',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const capitalizeValue = (value) => {
    if (!value) return '';
    const special = {
        soft_skills: 'Soft Skills', all_employees: 'All Employees',
        super_admin: 'Super Admin', hr_manager: 'HR Manager',
        hr_executive: 'HR Executive', sales_manager: 'Sales Manager',
        sales_executive: 'Sales Executive', dispatch_manager: 'Dispatch Manager',
        support_manager: 'Support Manager',
    };
    return special[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0m';
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 && hours === 0) parts.push(`${secs}s`);
    return parts.length > 0 ? parts.join(' ') : '0m';
};

const DIFFICULTY_CONFIG = {
    beginner: { label: 'Beginner', color: C.green, bg: C.green100, icon: '🌱' },
    intermediate: { label: 'Intermediate', color: C.yellow, bg: '#fef3c7', icon: '⚡' },
    advanced: { label: 'Advanced', color: C.red, bg: C.redLight, icon: '🔥' },
};

const STATUS_CONFIG = {
    published: { label: 'Published', color: C.green, bg: C.green100 },
    draft: { label: 'Draft', color: C.yellow, bg: '#fef3c7' },
    archived: { label: 'Archived', color: C.gray500, bg: C.gray100 },
};

const LESSON_TYPE_CONFIG = {
    video: { label: 'Video', bg: C.blue100, color: C.blue700, icon: Video },
    youtube: { label: 'YouTube', bg: C.redLight, color: C.redDark, icon: Video },
    file: { label: 'Document', bg: C.purpleLight, color: C.purple, icon: FileText },
    document: { label: 'Document', bg: C.purpleLight, color: C.purple, icon: FileText },
    quiz: { label: 'Quiz', bg: C.orangeLight, color: C.orange, icon: CheckCircle },
    assessment: { label: 'Assessment', bg: C.orangeLight, color: C.orange, icon: CheckCircle },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const InfoBadge = ({ icon: Icon, label, value, color = C.green }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 12, padding: '12px 16px',
    }}>
        <div style={{
            width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.15)', flexShrink: 0,
        }}>
            <Icon size={16} color="#fff" />
        </div>
        <div>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</p>
            <p style={{ fontSize: 13, color: '#fff', fontWeight: 600, margin: 0 }}>{value || '—'}</p>
        </div>
    </div>
);

const SectionCard = ({ title, icon: Icon, children, accent = C.green }) => (
    <div style={{
        background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 20,
    }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '16px 22px', borderBottom: `1px solid ${C.gray100}`,
            background: C.gray50,
        }}>
            <div style={{
                width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${accent}18`,
            }}>
                <Icon size={16} color={accent} />
            </div>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: C.gray800, margin: 0 }}>{title}</h3>
        </div>
        <div style={{ padding: '18px 22px' }}>{children}</div>
    </div>
);

const LessonTypeIcon = ({ type }) => {
    const cfg = LESSON_TYPE_CONFIG[type] || { bg: C.gray100, color: C.gray500, icon: FileText };
    const Icon = cfg.icon;
    return (
        <div style={{
            width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: cfg.bg, flexShrink: 0,
        }}>
            <Icon size={14} color={cfg.color} />
        </div>
    );
};

const LessonTypeBadge = ({ type }) => {
    const cfg = LESSON_TYPE_CONFIG[type] || { label: capitalizeFirst(type || ''), bg: C.gray100, color: C.gray600 };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700,
            background: cfg.bg, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
            {cfg.label}
        </span>
    );
};

const LessonCard = ({ lesson, lessonIndex, onPlayVideo, onViewLesson }) => {
    const videoFile = lesson.files?.find(f => f.type === 'video' && f.url);
    const ytFile = lesson.files?.find(f => f.type === 'youtube' && f.url);
    const docFile = lesson.files?.find(f => (f.type === 'file' || f.type === 'document') && f.url);

    const getYoutubeVideoId = (url) => {
        if (!url) return '';
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : '';
    };

    const isYoutubeUrl = (url) => {
        if (!url) return false;
        return url.includes('youtube.com') || url.includes('youtu.be');
    };

    const isYt = lesson.type === 'youtube' || isYoutubeUrl(lesson.videoUrl) || isYoutubeUrl(ytFile?.url);
    const ytVideoId = lesson.videoId || getYoutubeVideoId(lesson.videoUrl) || getYoutubeVideoId(ytFile?.url);
    const videoUrl = videoFile?.url || (lesson.type === 'video' ? lesson.videoUrl || lesson.url : (!isYt ? lesson.videoUrl : ''));

    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '12px 14px', background: '#fff',
            borderRadius: 9, border: `1px solid ${C.gray200}`,
            transition: 'box-shadow 0.15s, border-color 0.15s',
        }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = C.green; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.gray200; }}
        >
            {/* Number */}
            <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: C.green100, color: C.greenMid, fontSize: 11, fontWeight: 700,
            }}>
                {lessonIndex + 1}
            </div>

            <LessonTypeIcon type={lesson.type} />

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, marginBottom: 3 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: C.gray800, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                        {lesson.title || 'Untitled Lesson'}
                    </p>
                    {lesson.isGraded && (
                        <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: C.yellowLight, color: C.yellowDark }}>
                            Graded
                        </span>
                    )}
                </div>

                {lesson.description && (
                    <div
                        className="rich-text-content"
                        style={{ fontSize: 12, color: C.gray500, margin: '0 0 5px', wordBreak: 'break-word', lineHeight: 1.5 }}
                        dangerouslySetInnerHTML={{ __html: lesson.description }}
                    />
                )}

                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <LessonTypeBadge type={lesson.type} />

                    {/* View Button */}
                    <button
                        onClick={() => onViewLesson(lesson)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                            background: C.green, color: '#fff', border: 'none', cursor: 'pointer',
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = C.greenDark}
                        onMouseLeave={e => e.currentTarget.style.background = C.green}
                    >
                        <BookOpen size={10} /> View
                    </button>

                    {/* Play – uploaded video */}
                    {!isYt && videoUrl && (
                        <button
                            onClick={() => onPlayVideo({ title: lesson.title, type: 'video', url: videoUrl })}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                background: C.blue, color: '#fff', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = C.blueDark}
                            onMouseLeave={e => e.currentTarget.style.background = C.blue}
                        >
                            <Play size={10} fill="currentColor" /> Play
                        </button>
                    )}

                    {/* YouTube Watch */}
                    {isYt && (ytVideoId || ytFile) && (
                        <button
                            onClick={() => onPlayVideo({ title: lesson.title, type: 'youtube', videoId: ytVideoId || getYoutubeVideoId(ytFile?.url) })}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'}
                            onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}
                        >
                            <Play size={10} fill="currentColor" /> Watch
                        </button>
                    )}

                    {/* Document */}
                    {(lesson.type === 'file' || lesson.type === 'document') && docFile && (
                        <a
                            href={docFile.url} target="_blank" rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                background: C.purple, color: '#fff', textDecoration: 'none',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#6d28d9'}
                            onMouseLeave={e => e.currentTarget.style.background = C.purple}
                        >
                            <Download size={10} /> View Doc
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

const ModuleAccordion = ({ module, moduleIndex, onPlayVideo, onViewLesson }) => {
    const [expanded, setExpanded] = useState(moduleIndex === 0);
    const lessonCount = module.lessons?.length || 0;
    const totalDuration = (module.lessons || []).reduce((acc, l) => acc + (l.duration || 0), 0);

    return (
        <div style={{
            border: `1px solid ${expanded ? C.green : C.gray200}`,
            borderRadius: 12, marginBottom: 12, overflow: 'hidden',
            boxShadow: expanded ? `0 4px 14px rgba(34,197,94,0.10)` : '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.2s, border-color 0.2s',
        }}>
            {/* Module header */}
            <button
                onClick={() => setExpanded(v => !v)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '15px 18px',
                    background: expanded
                        ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                        : C.gray50,
                    border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Module number badge */}
                    <div style={{
                        width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: expanded ? C.green : C.green100,
                        color: expanded ? '#fff' : C.greenMid,
                        fontSize: 13, fontWeight: 700, flexShrink: 0, transition: 'all 0.2s',
                    }}>
                        {moduleIndex + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                            fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0,
                            textTransform: 'capitalize', wordBreak: 'break-word',
                        }}>
                            {module.title || 'Untitled Module'}
                        </h4>
                        {module.description && (
                            <p style={{ fontSize: 12, color: C.gray500, margin: '2px 0 0', textTransform: 'capitalize' }}>
                                {module.description}
                            </p>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginLeft: 12 }}>
                    {lessonCount > 0 && (
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ fontSize: 12, fontWeight: 600, color: C.greenMid, margin: 0 }}>
                                {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
                            </p>
                            {totalDuration > 0 && (
                                <p style={{ fontSize: 11, color: C.gray400, margin: 0 }}>{formatDuration(totalDuration)}</p>
                            )}
                        </div>
                    )}
                    <div style={{
                        width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: expanded ? C.green100 : C.gray200,
                        transition: 'background 0.2s',
                    }}>
                        {expanded
                            ? <ChevronUp size={14} color={C.greenMid} />
                            : <ChevronDown size={14} color={C.gray600} />}
                    </div>
                </div>
            </button>

            {/* Lesson list */}
            {expanded && (
                <div style={{ padding: '12px 18px 16px', background: C.gray50 }}>
                    {lessonCount > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <p style={{
                                fontSize: 10, fontWeight: 700, color: C.gray400,
                                textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4,
                            }}>
                                Lessons
                            </p>
                            {module.lessons.map((lesson, idx) => (
                                <LessonCard
                                    key={lesson.id || lesson.lessonId || idx}
                                    lesson={lesson}
                                    lessonIndex={idx}
                                    onPlayVideo={onPlayVideo}
                                    onViewLesson={onViewLesson}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ fontSize: 13, color: C.gray400, fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
                            No lessons added yet
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// Video Modal
const VideoModal = ({ lesson, onClose }) => {
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onClose]);

    const getYoutubeVideoId = (url) => {
        if (!url) return '';
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : '';
    };

    const isYoutube = lesson.type === 'youtube' || (lesson.url && (lesson.url.includes('youtube.com') || lesson.url.includes('youtu.be')));
    const ytVideoId = lesson.videoId || getYoutubeVideoId(lesson.url || lesson.videoUrl);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(4px)',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: '90vw', maxWidth: 860,
                    background: '#0f172a', borderRadius: 16, overflow: 'hidden',
                    boxShadow: '0 40px 100px rgba(0,0,0,0.6)',
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: '#020617', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: isYoutube ? '#dc262620' : `${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Video size={14} color={isYoutube ? '#ef4444' : C.blue} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520 }}>
                            {lesson.title}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer',
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                    >
                        <X size={14} color="#94a3b8" />
                    </button>
                </div>

                {isYoutube && ytVideoId ? (
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                        <iframe
                            title={lesson.title}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                            src={`https://www.youtube.com/embed/${ytVideoId}`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                ) : (lesson.url || lesson.videoUrl) ? (
                    <video controls src={lesson.url || lesson.videoUrl}
                        style={{ width: '100%', maxHeight: '72vh', background: '#000', display: 'block' }}>
                        Your browser does not support the video tag.
                    </video>
                ) : (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        No video source found.
                    </div>
                )}
            </div>
        </div>
    );
};

// Full Screen Lesson Detail Modal
const LessonDetailModal = ({ lesson, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const videoFile = lesson.files?.find(f => f.type === 'video' && f.url) || (lesson.type === 'video' && lesson.url ? { url: lesson.url } : null);
    const ytFile = lesson.files?.find(f => f.type === 'youtube' && f.url) || (lesson.type === 'youtube' && lesson.videoUrl ? { url: lesson.videoUrl } : null);
    const docFile = lesson.files?.find(f => (f.type === 'file' || f.type === 'document' || f.type === 'doc') && f.url) || (['file', 'document'].includes(lesson.type) && lesson.url ? { url: lesson.url } : null);

    const getYoutubeVideoId = (url) => {
        if (!url) return '';
        if (url.length === 11) return url;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : '';
    };

    const isYoutubeUrl = (url) => {
        if (!url) return false;
        return url.includes('youtube.com') || url.includes('youtu.be');
    };

    const isYt = lesson.type === 'youtube' || isYoutubeUrl(lesson.videoUrl) || isYoutubeUrl(ytFile?.url);
    const ytVideoId = lesson.videoId || getYoutubeVideoId(lesson.videoUrl) || getYoutubeVideoId(ytFile?.url);
    const videoUrl = videoFile?.url || (lesson.type === 'video' ? lesson.videoUrl || lesson.url : (!isYt ? lesson.videoUrl : ''));

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', flexDirection: 'column',
            background: '#0f172a', color: '#f8fafc', overflow: 'hidden'
        }}>
            {/* Header top bar */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 24px', background: '#020617', borderBottom: '1px solid rgba(255,255,255,0.08)',
                flexShrink: 0
            }}>
                <button
                    onClick={onClose}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
                        border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13,
                        fontWeight: 600, transition: 'color 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                >
                    <ArrowLeft size={16} /> Back to Course
                </button>
                <div style={{ textAlign: 'center', flex: 1, margin: '0 24px', minWidth: 0 }}>
                    <h2 style={{
                        fontSize: 16, fontWeight: 700, margin: 0, color: '#f8fafc',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                        {lesson.title || 'Untitled Lesson'}
                    </h2>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)',
                        border: 'none', cursor: 'pointer', transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                >
                    <X size={16} color="#94a3b8" />
                </button>
            </div>

            {/* Main content body */}
            <div style={{
                flex: 1, overflowY: 'auto', padding: '32px 24px', display: 'flex',
                justifyContent: 'center', background: '#0f172a'
            }}>
                <div style={{ width: '100%', maxWidth: 1120, display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {/* Responsive flex layout */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 28
                    }}>
                        {/* Main area */}
                        <div style={{ flex: '1 1 600px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {/* Details (Title & Description) */}
                            <div style={{
                                background: '#1e293b', borderRadius: 12, padding: '24px',
                                border: '1px solid rgba(255,255,255,0.06)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    <LessonTypeBadge type={lesson.type} />
                                    {lesson.isGraded && (
                                        <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: C.yellowLight, color: C.yellowDark }}>
                                            GRADED
                                        </span>
                                    )}
                                    {lesson.duration > 0 && (
                                        <span style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Clock size={12} /> {lesson.duration} min
                                        </span>
                                    )}
                                </div>
                                <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 16px', color: '#f8fafc', textTransform: 'capitalize' }}>
                                    {lesson.title || 'Untitled Lesson'}
                                </h1>
                                {lesson.description && (
                                    <div>
                                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>
                                            Description
                                        </h3>
                                        <div
                                            className="rich-text-content"
                                            style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.6, margin: 0 }}
                                            dangerouslySetInnerHTML={{ __html: lesson.description }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Lesson Content Section */}
                            {lesson.content && (
                                <div style={{
                                    background: '#1e293b', borderRadius: 12, padding: '24px',
                                    border: '1px solid rgba(255,255,255,0.06)'
                                }}>
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px' }}>
                                        Lesson Content
                                    </h3>
                                    <div
                                        className="rich-text-content"
                                        style={{ fontSize: 14, color: '#e2e8f0', lineHeight: 1.7 }}
                                        dangerouslySetInnerHTML={{ __html: lesson.content }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Sidebar details area */}
                        <div style={{ flex: '1 1 300px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {/* Course Context Card */}
                            <div style={{
                                background: '#1e293b', borderRadius: 12, padding: '20px',
                                border: '1px solid rgba(255,255,255,0.06)'
                            }}>
                                <h4 style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px' }}>
                                    Lesson Details
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Type</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', textTransform: 'capitalize' }}>{lesson.type}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Video Display Container */}
                            {(lesson.type === 'youtube' || lesson.type === 'video' || isYt || videoUrl) ? (
                                <div style={{
                                    position: 'relative',
                                    borderRadius: 12, overflow: 'hidden', background: '#000',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                                }}>
                                    {isYt ? (
                                        ytVideoId ? (
                                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                                                <iframe
                                                    title={lesson.title}
                                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                                    src={`https://www.youtube.com/embed/${ytVideoId}`}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                />
                                            </div>
                                        ) : (
                                            <div style={{
                                                padding: '60px 20px', textAlign: 'center', background: '#1e293b',
                                                border: '1px solid rgba(255,255,255,0.06)', color: '#94a3b8',
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12
                                            }}>
                                                <Video size={40} color="#ef4444" style={{ opacity: 0.6 }} />
                                                <div>
                                                    <p style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>Invalid YouTube Video Link</p>
                                                    <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                                                        The URL "{ytFile?.url || lesson.videoUrl}" is not a valid YouTube video page.
                                                    </p>
                                                </div>
                                            </div>
                                        )
                                    ) : videoUrl ? (
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <video
                                                controls
                                                src={videoUrl}
                                                style={{ width: '100%', maxHeight: '65vh', display: 'block' }}
                                            >
                                                Your browser does not support the video tag.
                                            </video>
                                        </div>
                                    ) : (
                                        <div style={{
                                            padding: '60px 20px', textAlign: 'center', background: '#1e293b',
                                            border: '1px solid rgba(255,255,255,0.06)', color: '#94a3b8',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12
                                        }}>
                                            <Video size={40} color={C.blue} style={{ opacity: 0.6 }} />
                                            <div>
                                                <p style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', margin: '0 0 4px' }}>No Video File Found</p>
                                                <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                                                    This video lesson has no uploaded file.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : null}

                            {/* Download Document Card */}
                            {docFile && (
                                <div style={{
                                    background: '#1e293b', borderRadius: 12, padding: '20px',
                                    border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column',
                                    gap: 16
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{
                                            width: 36, height: 36, borderRadius: 8, background: `${C.purple}20`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                        }}>
                                            <FileText size={18} color={C.purple} />
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <p style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', margin: '0 0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {docFile.name || 'Lesson Document'}
                                            </p>
                                            <p style={{ fontSize: 10, color: '#94a3b8', margin: 0 }}>
                                                {docFile.size ? `${(docFile.size / 1024 / 1024).toFixed(2)} MB` : 'PDF / Document'}
                                            </p>
                                        </div>
                                    </div>
                                    <a
                                        href={docFile.url}
                                        download={docFile.name || 'document'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                            padding: '10px 14px', borderRadius: 8, background: C.purple, color: '#fff',
                                            fontWeight: 600, textDecoration: 'none', transition: 'background 0.2s', fontSize: 13,
                                            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#6d28d9'}
                                        onMouseLeave={e => e.currentTarget.style.background = C.purple}
                                    >
                                        <Download size={14} /> Download Document
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * CourseDetailPage
 *
 * Props:
 *   courseId (string)       – The course ID. Falls back to route param :id.
 *   courseData (object)     – Pre-loaded course (skips fetch).
 *   modules (array)         – Pre-loaded modules (skips fetch).
 *   onBack (func)           – Back button callback. Defaults to navigate(-1).
 */
const CourseDetailPage = ({ courseId: propCourseId, courseData: propCourseData, modules: propModules, onBack }) => {
    const params = useParams();
    const navigate = useNavigate();

    const courseId = propCourseId || params.id;
    const handleBack = onBack || (() => navigate(-1));

    const [course, setCourse] = useState(propCourseData || null);
    const [modules, setModules] = useState(propModules || []);
    const [loading, setLoading] = useState(!propCourseData);
    const [error, setError] = useState(null);
    const [playingLesson, setPlayingLesson] = useState(null);
    const [viewingLesson, setViewingLesson] = useState(null);

    // Aggregate stats
    const stats = React.useMemo(() => {
        const totalLessons = modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);
        const totalDuration = modules.reduce((a, m) => a + (m.lessons || []).reduce((b, l) => b + (l.duration || 0), 0), 0);
        const videoCount = modules.reduce((a, m) => a + (m.lessons || []).filter(l => l.type === 'video' || l.type === 'youtube').length, 0);
        const docCount = modules.reduce((a, m) => a + (m.lessons || []).filter(l => l.type === 'file' || l.type === 'document').length, 0);
        return { totalLessons, totalDuration, videoCount, docCount };
    }, [modules]);

    // Fetch when no pre-loaded data
    useEffect(() => {
        if (propCourseData) return;
        if (!courseId) { setError('No course ID provided'); setLoading(false); return; }

        const fetchCourse = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await trainingService.getCourseStructure(courseId);
                if (res.success && res.data) {
                    if (res.data.course) setCourse(res.data.course);
                    setModules(res.data.modules || []);
                } else {
                    setError(res.error || 'Failed to load course details');
                }
            } catch {
                setError('Failed to load course details. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchCourse();
    }, [courseId, propCourseData]);

    useEffect(() => { if (propModules) setModules(propModules); }, [propModules]);

    const handlePlayVideo = useCallback((payload) => setPlayingLesson(payload), []);

    // Derived fields
    const diffCfg = DIFFICULTY_CONFIG[course?.difficulty || course?.level] || DIFFICULTY_CONFIG.beginner;
    const statusCfg = STATUS_CONFIG[course?.status] || STATUS_CONFIG.draft;
    const courseType = typeof course?.courseType === 'object' ? course.courseType?.name : course?.type || '';
    const objectives = course?.learningObjectives || course?.objectives || [];
    const prerequisites = course?.prerequisites;
    const instructor = typeof course?.instructor === 'object' ? course.instructor?.name : course?.instructor;
    const tags = course?.skills || course?.tags || [];

    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: `3px solid ${C.green100}`, borderTop: `3px solid ${C.green}`,
                    animation: 'spin 0.8s linear infinite',
                }} />
                <p style={{ fontSize: 13, color: C.gray500, fontWeight: 500 }}>Loading course details…</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ── Error ────────────────────────────────────────────────────────────────
    if (error) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.redLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AlertCircle size={24} color={C.red} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, margin: 0 }}>Failed to Load Course</h3>
                <p style={{ fontSize: 13, color: C.gray500, textAlign: 'center', maxWidth: 340, margin: 0 }}>{error}</p>
                <button
                    onClick={handleBack}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 8,
                        padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: C.green, color: '#fff', fontSize: 13, fontWeight: 600,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.greenMid}
                    onMouseLeave={e => e.currentTarget.style.background = C.green}
                >
                    <ArrowLeft size={14} /> Go Back
                </button>
            </div>
        );
    }

    // ── Main Render ──────────────────────────────────────────────────────────
    return (
        <div style={{ minHeight: '100vh', background: C.gray50 }}>

            {/* ── Hero Header ──────────────────────────────────────────────── */}
            <div style={{ background: C.heroBg, padding: '0 0 28px', position: 'relative', overflow: 'hidden' }}>
                {/* decorative rings */}
                <div style={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', top: -140, right: -80, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', bottom: -80, left: 60, pointerEvents: 'none' }} />

                <div style={{ margin: '0 auto', padding: '0 24px' }}>
                    {/* Back btn */}
                    <div style={{ paddingTop: 22, paddingBottom: 6 }} className='flex justify-between'>
                        <button
                            onClick={handleBack}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                padding: '6px 14px', borderRadius: 7,
                                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                                color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                        >
                            <ArrowLeft size={13} /> Back to Courses
                        </button>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '14px 0 12px' }}>
                            {courseType && (
                                <span style={{
                                    padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                    background: 'rgba(255,255,255,0.15)', color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.25)',
                                }}>
                                    {capitalizeValue(courseType)}
                                </span>
                            )}
                            <span style={{
                                padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                background: statusCfg.bg, color: statusCfg.color,
                                border: `1px solid ${statusCfg.color}40`,
                            }}>
                                {statusCfg.label}
                            </span>
                            {course?.isMandatory && (
                                <span style={{
                                    padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                    background: 'rgba(251,191,36,0.18)', color: '#fbbf24',
                                    border: '1px solid rgba(251,191,36,0.3)',
                                }}>
                                    ⚑ Mandatory
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Badges row */}


                    <h1 style={{
                        fontSize: 'clamp(19px, 3vw, 30px)', fontWeight: 800, color: '#fff',
                        margin: '0 0 8px', lineHeight: 1.25, textTransform: 'capitalize', wordBreak: 'break-word',
                    }}>
                        {course?.title || 'Untitled Course'}
                    </h1>

                    {course?.code && (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontFamily: 'monospace', marginBottom: 6 }}>
                            Code: <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{course.code}</span>
                        </p>
                    )}

                    {/* Instructor */}
                    {instructor && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                            <div style={{
                                width: 30, height: 30, borderRadius: '50%',
                                background: 'linear-gradient(135deg,#bbf7d0,#22c55e)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 13, fontWeight: 700, color: C.greenDark,
                            }}>
                                {instructor.charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                                {instructor}
                            </span>
                        </div>
                    )}

                    {/* Info badges grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, maxWidth: 720 }}>
                        <InfoBadge icon={Layers} label="Modules" value={`${modules.length} module${modules.length !== 1 ? 's' : ''}`} />
                        <InfoBadge icon={BookOpen} label="Lessons" value={`${stats.totalLessons} lesson${stats.totalLessons !== 1 ? 's' : ''}`} />
                        {course?.duration > 0 && (
                            <InfoBadge icon={Clock} label="Duration" value={formatDuration(course.duration)} />
                        )}
                        <InfoBadge icon={BarChart2} label="Difficulty" value={`${diffCfg.icon} ${diffCfg.label}`} />
                        {course?.enrolledCount > 0 && (
                            <InfoBadge icon={Users} label="Enrolled" value={`${course.enrolledCount} learner${course.enrolledCount !== 1 ? 's' : ''}`} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Body ─────────────────────────────────────────────────────── */}
            <div style={{ margin: '0 auto', padding: '28px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>

                    {/* Left – main content */}
                    <div>
                        {/* About */}
                        {(course?.description || course?.summary) && (
                            <SectionCard title="About This Course" icon={Info} accent={C.blue}>
                                <p style={{ fontSize: 13, color: C.gray700, lineHeight: 1.8, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                                    {course.description || course.summary}
                                </p>
                            </SectionCard>
                        )}

                        {/* Learning Objectives */}
                        {objectives.length > 0 && objectives.some(o => (o || '').trim()) && (
                            <SectionCard title="Learning Objectives" icon={Target} accent={C.green}>
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {objectives.filter(o => (o || '').trim()).map((obj, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                                                background: C.green100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <CheckCircle size={12} color={C.greenMid} />
                                            </div>
                                            <span style={{ fontSize: 13, color: C.gray700, textTransform: 'capitalize', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                {obj}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </SectionCard>
                        )}

                        {/* Prerequisites */}
                        {prerequisites && (
                            <SectionCard title="Prerequisites" icon={AlertCircle} accent={C.yellow}>
                                <p style={{ fontSize: 13, color: C.gray700, lineHeight: 1.7, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                                    {typeof prerequisites === 'string'
                                        ? prerequisites
                                        : Array.isArray(prerequisites)
                                            ? prerequisites.join(', ')
                                            : String(prerequisites)}
                                </p>
                            </SectionCard>
                        )}

                        {/* Course Structure */}
                        <SectionCard title="Course Structure" icon={BookOpen} accent={C.green}>
                            {modules.length > 0 ? (
                                <>
                                    {/* Summary bar */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
                                        padding: '11px 14px', background: C.green50, borderRadius: 9,
                                        border: `1px solid ${C.green100}`,
                                    }}>
                                        <Layers size={15} color={C.greenMid} />
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: 13, fontWeight: 600, color: C.greenMid, margin: '0 0 3px' }}>
                                                {modules.length} Module{modules.length !== 1 ? 's' : ''} · {stats.totalLessons} Lesson{stats.totalLessons !== 1 ? 's' : ''}
                                            </p>
                                            <div style={{ display: 'flex', gap: 12 }}>
                                                {stats.videoCount > 0 && (
                                                    <span style={{ fontSize: 11, color: C.gray500 }}>📹 {stats.videoCount} video{stats.videoCount !== 1 ? 's' : ''}</span>
                                                )}
                                                {stats.docCount > 0 && (
                                                    <span style={{ fontSize: 11, color: C.gray500 }}>📄 {stats.docCount} doc{stats.docCount !== 1 ? 's' : ''}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {modules.map((module, idx) => (
                                        <ModuleAccordion
                                            key={module.id || module._id || idx}
                                            module={module}
                                            moduleIndex={idx}
                                            onPlayVideo={handlePlayVideo}
                                            onViewLesson={setViewingLesson}
                                        />
                                    ))}
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '28px 0', color: C.gray400 }}>
                                    <BookOpen size={32} style={{ marginBottom: 10, opacity: 0.35 }} />
                                    <p style={{ fontSize: 13, margin: 0 }}>No modules have been added to this course yet.</p>
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Right – sidebar */}
                    <div style={{ position: 'sticky', top: 24 }}>
                        {/* Course Info card */}
                        <div style={{
                            background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 18,
                        }}>
                            <div style={{
                                padding: '14px 18px', borderBottom: `1px solid ${C.gray100}`,
                                background: C.gray50, display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                <Info size={14} color={C.blue} />
                                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>Course Info</h3>
                            </div>
                            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {[
                                    { label: 'Course Code', value: course?.code || course?.courseId, mono: true },
                                    { label: 'Type', value: courseType ? capitalizeValue(courseType) : null },
                                    { label: 'Category', value: course?.category ? capitalizeValue(course.category) : null },
                                    { label: 'Difficulty', value: diffCfg ? `${diffCfg.icon} ${diffCfg.label}` : null },
                                    { label: 'Status', value: statusCfg?.label },
                                    { label: 'Duration', value: course?.duration ? formatDuration(course.duration) : null },
                                    { label: 'Instructor', value: instructor },
                                    { label: 'Mandatory', value: course?.isMandatory !== undefined ? (course.isMandatory ? 'Yes' : 'No') : null },
                                    { label: 'Max Attempts', value: course?.assessment?.maxAttempts || course?.maxAttempts },
                                    { label: 'Certificate', value: course?.certificate?.enabled ? 'Awarded on Pass' : null },
                                    {
                                        label: 'Created',
                                        value: course?.createdAt
                                            ? new Date(course.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : null,
                                    },
                                ].filter(item => item.value !== null && item.value !== undefined && item.value !== '').map(({ label, value, mono }) => (
                                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <span style={{ fontSize: 12, color: C.gray500, fontWeight: 500, flexShrink: 0 }}>{label}</span>
                                        <span style={{
                                            fontSize: 12, color: C.gray800, fontWeight: 600,
                                            textAlign: 'right', wordBreak: 'break-word',
                                            fontFamily: mono ? 'monospace' : 'inherit',
                                            background: mono ? C.gray100 : 'transparent',
                                            padding: mono ? '2px 6px' : 0,
                                            borderRadius: mono ? 4 : 0,
                                        }}>
                                            {String(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Tags */}
                        {tags.length > 0 && (
                            <div style={{
                                background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '14px 18px', marginBottom: 18,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <Tag size={13} color={C.blue} />
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>Skills & Tags</h3>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {tags.map((tag, i) => (
                                        <span key={i} style={{
                                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                            background: C.blue50, color: C.blue, border: `1px solid ${C.blue100}`,
                                        }}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Content summary card */}
                        <div style={{
                            background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                            borderRadius: 12, padding: '18px', marginBottom: 18,
                            boxShadow: `0 8px 20px rgba(34,197,94,0.28)`,
                        }}>
                            <h3 style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                Content Summary
                            </h3>
                            {[
                                { label: 'Modules', value: modules.length, icon: Layers },
                                { label: 'Lessons', value: stats.totalLessons, icon: BookOpen },
                                { label: 'Video Lessons', value: stats.videoCount, icon: Video },
                                { label: 'Documents', value: stats.docCount, icon: FileText },
                            ].map(({ label, value, icon: Icon }) => (
                                <div key={label} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.12)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Icon size={13} color="rgba(255,255,255,0.75)" />
                                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)' }}>{label}</span>
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Video Modal */}
            {playingLesson && (
                <VideoModal lesson={playingLesson} onClose={() => setPlayingLesson(null)} />
            )}

            {/* Lesson Detail Modal */}
            {viewingLesson && (
                <LessonDetailModal lesson={viewingLesson} onClose={() => setViewingLesson(null)} />
            )}
        </div>
    );
};

export default CourseDetailPage;
